import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CandidateOAuthProvider } from '@hr-outsourcing/shared';
import { careersApi, oauthStartUrl } from '@/careers/lib/careersApi';
import { safeNextPath } from '@/careers/lib/redirect';
import { CAREERS_QUERY_KEYS } from '@/careers/constants';
import { Button } from '@/components/ui';

const GoogleIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true' className='h-[18px] w-[18px]'>
    <path fill='#4285F4' d='M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.8 3-4.3 3-7.4Z' />
    <path
      fill='#34A853'
      d='M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.2H3.1v2.6A10 10 0 0 0 12 22Z'
    />
    <path fill='#FBBC05' d='M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.1a10 10 0 0 0 0 9l3.3-2.6Z' />
    <path
      fill='#EA4335'
      d='M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.8A9.7 9.7 0 0 0 3.1 7.5l3.3 2.6C7.2 7.7 9.4 5.9 12 5.9Z'
    />
  </svg>
);

/** "Continue with Google" for candidates — only when the server has its credentials. */
export const CandidateOAuthButtons = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { data: providers = [] } = useQuery({
    queryKey: CAREERS_QUERY_KEYS.oauthProviders,
    queryFn: careersApi.getOAuthProviders,
    staleTime: Infinity,
    retry: false,
  });
  if (providers.length === 0) return null;

  const providerButton: Record<CandidateOAuthProvider, { label: string; icon: ReactNode }> = {
    [CandidateOAuthProvider.GOOGLE]: { label: t('careers.oauth.google'), icon: <GoogleIcon /> },
  };

  const next = safeNextPath(search);
  // Giriş / Kayıt tasarımlarındaki "Google ile devam et" butonu ve "veya e-posta ile" ayracı.
  return (
    <div className='grid gap-2.5'>
      {providers.map((provider) => (
        <Button
          key={provider}
          asChild
          variant='outline'
          className='h-[50px] w-full gap-3 rounded-[12px] border-[var(--public-line-mid)] bg-white text-[15px] font-semibold text-public-ink hover:bg-public-warm hover:text-public-ink'
        >
          <a href={oauthStartUrl(provider, next)}>
            {providerButton[provider].icon}
            {providerButton[provider].label}
          </a>
        </Button>
      ))}
      <div className='my-2.5 mb-[22px] flex items-center gap-3.5 text-[12.5px] text-public-dim'>
        <span aria-hidden='true' className='h-px flex-1 bg-[var(--public-line)]' />
        {t('careers.oauth.or')}
        <span aria-hidden='true' className='h-px flex-1 bg-[var(--public-line)]' />
      </div>
    </div>
  );
};
